/*
EXAMPLE USAGE
*
* 
*
Include this script in the parent section where you are using this component: 
<script src="{{ 'accordion.js' | asset_url }}" defer></script>
*
<content-accordion> 
  <ul> // every accordion item renders as `li` element for ADA
    # for item in iterable
    {% liquid 
      render 'component-accordion',
      label<string>: [item.label], // toggle button label
      content<string/html>: [item.content], // the content inside the accordion
      id<string>: [item.unique_id], // must be unique per accordion
      default<bool>: true // optional - pass in to have the accordion item open by default
    %}
  </ul>
</content-accordion>
*/

if (!customElements.get('content-accordion')) {
  // Use to render standard accordions like the snippet above, but with js
  window.renderAccordionItem = ({ attr, label, content, id, isOpenByDefault, icon = 'chevron'}) => `
    <li 
      class="
        ${isOpenByDefault ? 'active' :''} 
        sp-block
        sp-border-lightGrey
        sp-border-t 
        first:sp-border-t-0
        last:sp-border-b 
        [&_.sp-rotate]:sp-transform
        ${icon === 'plus' ? '[&.active_.sp-rotate]:-sp-rotate-90' : '[&.active_.sp-rotate]:-sp-rotate-90'}
        [&:not(.active)_[data-accordion-content]]:sp-max-h-0 
        [&_[data-accordion-content]]:sp-max-h-[var(--accordion-content-height)]
        [&_[data-accordion-content]]:sp-duration-500
        [&_[data-accordion-content]]:sp-transition-all
        [&_[data-accordion-content]]:sp-ease-out
        [&_.sp-rotate]:sp-duration-500
        [&_.sp-rotate]:sp-transition-all
        [&_.sp-rotate]:sp-ease-out
      "
      data-accordion-id="${id}"
      ${attr ? attr : ''}
    >
      <button 
        id="${id}"
        aria-controls="${id}"
        aria-expanded="${isOpenByDefault ? 'true' : 'false'}"
        data-accordion-toggle
        class="sp-flex sp-items-center sp-w-full sp-py-5" type="button" 
        aria-label="Open accordion for ${label}"
        type="button"
      >
        ${label}
        ${icon === 'plus' ? `
          <span aria-hidden="true" class="sp-p-0.5 sp-h-4 sp-w-4 sp-ml-auto sp-relative icon">
            <span class="sp-rotate sp-block sp-w-0.5 sp-h-full sp-absolute sp-left-1/2 sp-top-0 -sp-translate-x-1/2 sp-bg-black"></span>
            <span class="sp-block sp-w-full sp-h-0.5 sp-absolute sp-top-1/2 sp-left-0 -sp-translate-y-1/2 sp-bg-black"></span>
          </span>
        ` : `
          <span aria-hidden="true" class="sp-p-0.5 sp-h-4 sp-w-5 sp-ml-auto icon">
            <span class="sp-rotate sp-block sp-w-full sp-h-full">
              <svg style="transform: rotate(90deg);" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 11 19" fill="none">
                <path d="M1 17.5L9 9.5L1 1.5" stroke="currentColor" stroke-width="2"/>
              </svg>
            </span>
          </span>
        `}
      </button>
      <div 
        aria-hidden="${isOpenByDefault ? 'false' : 'true'}"
        id="${id}"
        role="region"
        aria-labelledby="${id}"
        data-accordion-content
        class="sp-overflow-hidden ${isOpenByDefault ? '' : 'sp-hidden'}" 
      >
        <div class="sp-pb-5">
          ${content}
        </div>
      </div>
    </li>
  `;
  
  customElements.define(
    'content-accordion',
    class Accordion extends HTMLElement {
      constructor() {
        super();
        this.allowMultipleActive;
      }
    
      setDefaultAccordions() {
        this.querySelectorAll('[data-accordion-id].active [data-accordion-content]').forEach((contentElement) => {
          contentElement.style.setProperty('--accordion-content-height', `${contentElement.scrollHeight}px`)
        });
      }
      
      connectedCallback() {
        // prep default open accordion height so they close smoothly the first time
        this.setDefaultAccordions(); 
        
        // if the children of the accordion are updated, recalculate height vars
        const observer = new MutationObserver(observerCallback.bind(this));
        observer.observe(this, { childList: true, subtree: true });
        
        function observerCallback(mutationList) {
          for (const mutation of mutationList) {
            if (mutation.type === 'childList') {
              this.setDefaultAccordions();
            }
          }
        }
        
        this.allowMultipleActive = this.dataset.allowMultipleActive === 'true';
      
        if (document.readyState === 'complete') {
          this.setDefaultAccordions();
        } else {
          document.addEventListener('DOMContentLoaded', this.setDefaultAccordions);
        }
        
        // hide elements, update aria attrs for acccessibility when they have finished transitioning out
        this.addEventListener('transitionend', (e) => {
          const accordionContent = e.target.closest('[data-accordion-id]:not(.active) [data-accordion-content]');
          if (!accordionContent) return;
          
          setTimeout(() => {
            accordionContent.classList.add('sp-hidden');
            accordionContent.setAttribute('aria-hidden', true);
            accordionContent.closest('[data-accordion-id]')
              ?.querySelector('[data-accordion-toggle]')
              ?.setAttribute('aria-expanded', false);
          }, 1);
        });
        
        this.addEventListener('click', (e) => {
          const accordionToggle = e.target.closest('[data-accordion-toggle]');
          if (!accordionToggle) return;
          
          const parentElement = accordionToggle.closest('[data-accordion-id]');
          const contentElement = parentElement.querySelector('[data-accordion-content]');
          const toggleElement = parentElement.querySelector('[data-accordion-toggle]');
          
          // if the accordion is not open, it's about to be. Prep it for a smooth animation/update the aria attrs
          if (!parentElement.classList.contains('active')) {
            contentElement.classList.remove('sp-hidden');
            contentElement.setAttribute('aria-hidden', false);
            toggleElement.setAttribute('aria-expanded', true);
          }
          
          setTimeout(() => {
            // open the current accordion
            contentElement.style.setProperty('--accordion-content-height', `${contentElement.scrollHeight}px`)
            parentElement.classList.toggle('active');

            if (this.allowMultipleActive) return;
            
            // close all the other accordions that might be open
            [...this.querySelectorAll('[data-accordion-id].active')]
              .filter((node) => !node.isSameNode(parentElement))
              .forEach((inactiveParentElement) => {
                inactiveParentElement.classList.remove('active');
                inactiveParentElement.querySelector('[data-accordion-content]')
                  .setAttribute('aria-hidden', true);
                inactiveParentElement.querySelector('[data-accordion-toggle]')
                  .setAttribute('aria-expanded', false);
              });
          }, 1);
        })
      }
    }
  );
}